import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import DotboxLogo from '@/components/common/DotboxLogo';
import { Loader2, ArrowLeft, MailCheck } from 'lucide-react';

const formatApiError = (e, fallback = 'Something went wrong') => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || JSON.stringify(d)).join('; ');
  if (detail && typeof detail === 'object') return detail.msg || JSON.stringify(detail);
  return fallback;
};

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        email: email.trim().toLowerCase(),
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not process request. Try again later.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A1024] px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <DotboxLogo className="h-10 mx-auto mb-4" />
        </div>
        <Card data-testid="forgot-password-card">
          {!submitted ? (
            <>
              <CardHeader>
                <CardTitle className="text-white">Forgot your password?</CardTitle>
                <CardDescription>
                  Enter the email you used to sign up and we&apos;ll send you a link to reset your password.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading}
                      className="h-11"
                      data-testid="forgot-password-email-input"
                      autoFocus
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading || !email}
                    className="w-full h-11 bg-[#DC2626] hover:bg-[#B91C1C]"
                    data-testid="forgot-password-submit-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send Reset Link'
                    )}
                  </Button>
                </form>
                <div className="mt-6 text-center">
                  <Link
                    to="/login"
                    className="text-sm text-red-400 hover:text-red-300 inline-flex items-center gap-1"
                    data-testid="back-to-login-link"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
                  </Link>
                </div>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader>
                <div className="flex items-center gap-2 text-white">
                  <MailCheck className="h-6 w-6 text-emerald-400" />
                  <CardTitle>Check your inbox</CardTitle>
                </div>
                <CardDescription>
                  If an account exists for <strong>{email}</strong>, we&apos;ve sent a password reset link.
                  It will expire in 60 minutes.
                </CardDescription>
              </CardHeader>
              <CardContent data-testid="forgot-password-success">
                <p className="text-sm text-slate-300 mb-4">
                  Didn&apos;t receive an email? Check your spam folder, then{' '}
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    className="text-red-400 hover:text-red-300 underline"
                    data-testid="forgot-password-try-again"
                  >
                    try again
                  </button>
                  .
                </p>
                <Link
                  to="/login"
                  className="text-sm text-red-400 hover:text-red-300 inline-flex items-center gap-1"
                >
                  <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
                </Link>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
